import { useLingui } from "@lingui/react/macro";
import { Check, Copy } from "lucide-react";
import { type MouseEvent, useEffect, useState } from "react";

import type { ButtonSize, ButtonVariant } from "./base/button";

import { Button } from "./button";

interface Props {
  value: string;
  label?: string;
  copiedLabel?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows only the copy icon; `label` then names it and is its tooltip. */
  iconOnly?: boolean;
}

/**
 * A Button that copies `value` and says so for 1.5 s — its label turns to
 * `copiedLabel`, or an icon-only one swaps its copy icon for a green check. In
 * a table cell it is tertiary `xs` after the value.
 */
export function CopyButton({
  value,
  label,
  copiedLabel,
  variant,
  size,
  iconOnly,
}: Props) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  const name = copied
    ? (copiedLabel ?? t`Copied`)
    : (label ?? t`Copy to clipboard`);

  const copy = async (event: MouseEvent) => {
    // A copy inside a clickable row copies; it doesn't also open the row.
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch (error) {
      // Clipboard can reject (denied permission, insecure context). Don't
      // flash the confirmation, and swallow the rejection so it doesn't
      // surface as an unhandled promise rejection.
      console.warn("Copy to clipboard failed", error);
    }
  };

  return iconOnly ? (
    <Button
      variant={variant}
      size={size}
      icon={copied ? Check : Copy}
      label={name}
      className={copied ? "text-success-foreground" : undefined}
      onClick={copy}
    />
  ) : (
    <Button variant={variant} size={size} onClick={copy}>
      {name}
    </Button>
  );
}
